import type { Project } from '../types';
import type { SavedProject } from '../types/project';
import { decryptData, encryptData, generateFileName } from './encryption';
import {
  packProjectFurnitureAssets,
  restoreProjectFurnitureAssets,
  type ModelSourceReader,
} from './tempProjectAssets';

export async function createPortableSavedProject(
  project: Project,
  projectName: string,
  now: number = Date.now(),
  sourceReader?: ModelSourceReader,
): Promise<SavedProject> {
  const packed = await packProjectFurnitureAssets(project, sourceReader);
  return {
    id: `project_${now}`,
    name: projectName,
    createdAt: now,
    updatedAt: now,
    data: packed.project,
    assets: packed.assets,
  };
}

export function restoreSavedProjectAssets(saved: SavedProject): SavedProject {
  return {
    ...saved,
    data: restoreProjectFurnitureAssets(saved.data, saved.assets),
  };
}

function validateSavedProject(value: unknown): SavedProject {
  const saved = value as SavedProject;
  if (!saved?.data || !saved.name) {
    throw new Error('文件数据格式不正确');
  }
  return saved;
}

/** Load an encrypted project file from a URL. */
export async function loadProjectFromUrl(url: string): Promise<SavedProject> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`加载文件失败: ${response.status}`);
    }
    const encryptedContent = await response.text();
    if (!encryptedContent) throw new Error('文件内容为空');

    const saved = validateSavedProject(decryptData(encryptedContent));
    return restoreSavedProjectAssets(saved);
  } catch (error) {
    console.error('Load project from URL failed:', error);
    throw new Error('加载项目文件失败，文件可能已损坏');
  }
}

/** Save a project as one encrypted, self-contained temporary file. */
export async function saveProjectToFile(project: Project, projectName: string): Promise<void> {
  try {
    // Complete all fallible asset work before creating any download side effect.
    const projectData = await createPortableSavedProject(project, projectName);
    const encryptedContent = encryptData(projectData);
    const fileName = generateFileName(projectName);
    const blob = new Blob([encryptedContent], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Save project to file failed:', error);
    throw error instanceof Error ? error : new Error('保存项目文件失败');
  }
}

/** Load an encrypted project from a user-selected `.kimap.tm` file. */
export async function loadProjectFromFile(file: File): Promise<SavedProject> {
  return new Promise((resolve, reject) => {
    if (!file.name.endsWith('.kimap.tm')) {
      reject(new Error('无效的文件格式，请选择 .kimap.tm 文件'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const encryptedContent = event.target?.result as string;
        if (!encryptedContent) throw new Error('文件内容为空');

        const saved = validateSavedProject(decryptData(encryptedContent));
        resolve(restoreSavedProjectAssets(saved));
      } catch (error) {
        console.error('Load project from file failed:', error);
        reject(new Error('加载项目文件失败，文件可能已损坏'));
      }
    };
    reader.onerror = () => reject(new Error('读取文件失败'));
    reader.readAsText(file);
  });
}

export function isValidKimapFile(fileName: string): boolean {
  return fileName.endsWith('.kimap.tm');
}

export function getFileInfo(file: File) {
  return {
    name: file.name,
    size: file.size,
    type: file.type,
    lastModified: file.lastModified,
  };
}
